import {
  CheckOutlined,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { bannerApi } from "api/banner.api";

import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import React, { useCallback, useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { Banner, BannerTypeTrans } from "types/banner";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { formatDate } from "utils/date";
import { $url } from "utils/url";
import { BannerModal } from "./components/BannerModal";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { areaStore } from "store/areaStore";
import { businessTypeStore } from "store/businessTypeStore";
// import { StaffModal } from "./components/StaffModal";

const { Column } = Table;
interface BannerQuery extends QueryParam {
  areaId: null | null;
}

export const BannerPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<BannerQuery>({
    page: 1,
    limit: 10,
    search: "",
    areaId: null,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Banner[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Partial<Banner>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
    areaStore.fetchList();
    businessTypeStore.fetchList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await bannerApi.findAll(query);
    setLoading(false);
    setData(res.data.banners);
    setTotal(res.data.total);
  };

  const handleDelete = async (banner: Banner) => {
    setLoading(true);
    try {
      await bannerApi.delete(banner.id);
      fetchData();
      message.info("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (banner: Banner) => {
    setLoading(true);
    try {
      if (banner.isVisible) {
        await bannerApi.update(banner.id, {
          banner: {
            isVisible: false,
          },
        });
        message.info("Ẩn thành công!");
      } else {
        await bannerApi.update(banner.id, {
          banner: {
            isVisible: true,
          },
        });
        message.info("Hiển thị thành công!");
      }
    } finally {
      fetchData();
      setLoading(false);
    }
  };

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm kiếm",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedBanner({});
                  }}
                  tone="primary"
                  icon={<PlusOutlined />}
                >
                  Tạo mới banner
                </AppButton>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Banner>
              loading={loading}
              pagination={false}
              rowKey="id"
              dataSource={data}
            >
          <Column
            title="Tiêu đề"
            dataIndex="title"
            key="title"
            render={(text, record: Banner) => {
              return (
                <div>
                  <img
                    src={$url(record.image)}
                    width={50}
                    style={{ objectFit: "contain", borderRadius: 5 }}
                    alt=""
                  />
                  <br />
                  <label htmlFor="">{text}</label>
                </div>
              );
            }}
          />

          <Column
            title="Loại"
            dataIndex="type"
            key="type"
            render={(text, record: Banner) => (
              <span>{BannerTypeTrans[record.type]}</span>
            )}
          />

          <Column
            title="Loại kinh doanh"
            dataIndex={["businessType", "name"]}
            key="businessTypeName"
          />

          <Column title="Khu vực" dataIndex={["area", "name"]} key="areaName" />

          <Column
            title="Trạng thái"
            dataIndex="isVisible"
            key="isVisible"
            render={(text) => (
              <Tag color={text ? "green" : "red"}>
                {text ? "Hiển thị" : "Bị ẩn"}
              </Tag>
            )}
          />

          <Column
            title="Ngày tạo"
            dataIndex="createdAt"
            key="phone"
            render={(text) => <span>{formatDate(text)}</span>}
          />

          <Column
            title="Thao tác"
            key="action"
            width={130}
            align="center"
            render={(text, record: Banner) => (
              <div className="flex gap-2 justify-center items-center">
                <Tooltip title="Cập nhật">
                  <ActionButton
                    aria-label="Cập nhật"
                    onClick={() => {
                      setSelectedBanner(record);
                      setVisibleModal(true);
                      setModalStatus("update");
                    }}
                  >
                    <EditOutlined style={{ color: "#6c84e5" }} />
                  </ActionButton>
                </Tooltip>
                <Tooltip title={record.isVisible ? "Ẩn" : "Hiển thị"}>
                  <Popconfirm
                    placement="topLeft"
                    title={`Xác nhận ${record.isVisible ? "ẩn" : "hiển thị"} banner này?`}
                    onConfirm={() => handleToggle(record)}
                    okText="Ok"
                    cancelText="Không"
                  >
                    <ActionButton aria-label={record.isVisible ? "Ẩn" : "Hiển thị"}>
                      {record.isVisible ? (
                        <EyeInvisibleOutlined style={{ color: "#f59e0b" }} />
                      ) : (
                        <CheckOutlined style={{ color: "#22c55e" }} />
                      )}
                    </ActionButton>
                  </Popconfirm>
                </Tooltip>
                <Tooltip title="Xóa">
                  <Popconfirm
                    placement="top"
                    title={"Xác nhận xóa banner này?"}
                    onConfirm={() => handleDelete(record)}
                    okText="Xóa"
                    cancelText="Không"
                  >
                    <ActionButton aria-label="Xóa">
                      <DeleteOutlined style={{ color: "#ef4444" }} />
                    </ActionButton>
                  </Popconfirm>
                </Tooltip>
              </div>
            )}
          />
            </AppTable>
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                query.page = page;
                query.limit = limit;
                setQuery({ ...query });
              }}
            />
          }
        />

      <BannerModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        banner={selectedBanner}
        status={modalStatus}
      />
    </div>
  );
});
